import { initializeApp } from "firebase/app";
import { getMessaging, isSupported } from "firebase/messaging";

const REQUIRED_CONFIG_FIELDS = ['apiKey', 'authDomain', 'projectId', 'storageBucket', 'messagingSenderId', 'appId'];

// Load Firebase config from the build-time environment variable.
//
// IMPORTANT: never swallow a failure here by returning {}. An empty config
// still lets initializeApp() succeed, and the breakage only surfaces later as
// a misleading `auth/invalid-api-key` from getAuth()/signInAnonymously(). The
// most common cause is a malformed VITE_FIREBASE_WEB_CONFIG secret (e.g. JSON
// with the quotes stripped: `{apiKey:AIza...}` instead of `{"apiKey":"AIza..."}`),
// so we throw loudly with the specific reason instead.
const getFirebaseConfig = () => {
  const configEnv = import.meta.env.VITE_FIREBASE_WEB_CONFIG;

  if (!configEnv) {
    throw new Error(
      'VITE_FIREBASE_WEB_CONFIG is not set. The build embedded no Firebase web config — check the FIREBASE_WEB_CONFIG secret for this environment.',
    );
  }

  let config;
  try {
    config = JSON.parse(configEnv);
  } catch (error) {
    throw new Error(
      `VITE_FIREBASE_WEB_CONFIG is not valid JSON: ${error.message}. ` +
        'Ensure the FIREBASE_WEB_CONFIG secret is single-line JSON with quotes on every key and value, ' +
        'e.g. {"apiKey":"...","authDomain":"...","projectId":"...","storageBucket":"...","messagingSenderId":"...","appId":"..."}.',
    );
  }

  const missingFields = REQUIRED_CONFIG_FIELDS.filter((field) => !config[field]);
  if (missingFields.length > 0) {
    throw new Error(`Firebase config is missing required field(s): ${missingFields.join(', ')}.`);
  }

  return config;
};

// Resolve the config and initialize Firebase. If the config is missing or
// malformed we log the real reason loudly here (rather than letting it
// resurface as a cryptic `auth/invalid-api-key` deep in an auth call) but
// still let the app boot — Firebase-backed features will be disabled.
let firebaseConfig = {};
try {
  firebaseConfig = getFirebaseConfig();
} catch (error) {
  console.error('[firebase] Invalid Firebase web config — Firebase features are disabled:', error.message);
}

const app = initializeApp(firebaseConfig);


// Initialize Firebase Cloud Messaging and get a reference to the service
const getMessagingInstance = async () => {
  try {
    const supported = await isSupported();
    if (supported) {
      return getMessaging(app);
    }
    return null;
  } catch (err) {
    return null;
  }
};

// Get VAPID key from environment or config
const getVapidKey = () => {
  const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY ||
                   process.env.FIREBASE_VAPID_KEY ||
                   firebaseConfig.vapidKey;
  
  return vapidKey;
};

// Export Firebase services for use in other parts of the application
export { app, firebaseConfig, getMessagingInstance };

// Export utility functions
export { getFirebaseConfig, getVapidKey };
